import React from "react";

import {
  Box,
  Chip,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import type { PodStatusSnapshot } from "../../hooks/usePodMonitor";

/** Format a K8s CPU quantity string for human-readable display.
 * Handles: "5m" → "5m", "500m" → "500m", "1" → "1 core", "14847717m" → "14.8 cores"
 * Also handles raw nanocore numbers that K8s metrics API may return. */
function formatCpuDisplay(value: string | undefined): string {
  if (!value) return "—";
  if (value.endsWith("m")) {
    const millicores = parseInt(value.slice(0, -1), 10);
    if (isNaN(millicores)) return value;
    if (millicores >= 1000) return `${(millicores / 1000).toFixed(1)} cores`;
    return `${millicores}m`;
  }
  if (value.endsWith("n")) {
    // Nanocores: convert to millicores
    const nanocores = parseInt(value.slice(0, -1), 10);
    if (isNaN(nanocores)) return value;
    const millicores = Math.round(nanocores / 1_000_000);
    if (millicores >= 1000) return `${(millicores / 1000).toFixed(1)} cores`;
    return `${millicores}m`;
  }
  // Plain number = cores
  const cores = parseFloat(value);
  if (isNaN(cores)) return value;
  if (cores >= 1 && cores === Math.floor(cores))
    return `${cores} core${cores !== 1 ? "s" : ""}`;
  return `${(cores * 1000).toFixed(0)}m`;
}

/** Format a K8s memory quantity string for human-readable display.
 * Handles: "12Mi" → "12 Mi", "1Gi" → "1 Gi", "1024Ki" → "1 Mi" */
function formatMemoryDisplay(value: string | undefined): string {
  if (!value) return "—";
  if (value.endsWith("Gi")) return `${parseFloat(value)} Gi`;
  if (value.endsWith("Mi")) return `${parseFloat(value)} Mi`;
  if (value.endsWith("Ki")) {
    const ki = parseInt(value.slice(0, -2), 10);
    if (isNaN(ki)) return value;
    if (ki >= 1024) return `${(ki / 1024).toFixed(1)} Mi`;
    return `${ki} Ki`;
  }
  // Raw bytes
  const bytes = parseInt(value, 10);
  if (!isNaN(bytes) && bytes > 0) {
    const mib = bytes / (1024 * 1024);
    if (mib >= 1024) return `${(mib / 1024).toFixed(1)} Gi`;
    return `${mib.toFixed(0)} Mi`;
  }
  return value;
}

function phaseChip(pod: PodStatusSnapshot) {
  if (pod.phase === "Running" && pod.ready) {
    return (
      <Chip label="Running" color="success" size="small" variant="filled" />
    );
  }
  if (pod.phase === "Running" && !pod.ready) {
    return (
      <Chip label="Not Ready" color="warning" size="small" variant="outlined" />
    );
  }
  if (pod.phase === "Pending") {
    return (
      <Chip label="Pending" color="info" size="small" variant="outlined" />
    );
  }
  if (pod.phase === "Failed") {
    return <Chip label="Failed" color="error" size="small" variant="filled" />;
  }
  if (pod.phase === "Succeeded") {
    return (
      <Chip label="Succeeded" color="default" size="small" variant="outlined" />
    );
  }
  return (
    <Chip label={pod.phase} color="default" size="small" variant="outlined" />
  );
}

function formatAge(startedAt: string | null): string {
  if (!startedAt) return "—";
  const seconds = Math.floor(
    (Date.now() - new Date(startedAt).getTime()) / 1000,
  );
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

interface PodRowProps {
  pod: PodStatusSnapshot;
}

export function PodRow({ pod }: PodRowProps) {
  return (
    <TableRow hover>
      <TableCell>
        <Tooltip title={pod.podName} placement="top">
          <Typography
            variant="body2"
            fontFamily="monospace"
            fontSize={12}
            sx={{
              maxWidth: 300,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {pod.podName}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>{phaseChip(pod)}</TableCell>
      <TableCell>
        <Typography variant="body2" fontFamily="monospace" fontSize={12}>
          {pod.restarts > 0 ? (
            <Box
              component="span"
              color={pod.restarts >= 3 ? "error.main" : "warning.main"}
            >
              {pod.restarts}
            </Box>
          ) : (
            "0"
          )}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary" fontSize={12}>
          {formatAge(pod.startedAt)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          fontFamily="monospace"
          fontSize={12}
          color="text.secondary"
        >
          {formatCpuDisplay(pod.cpu)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          fontFamily="monospace"
          fontSize={12}
          color="text.secondary"
        >
          {formatMemoryDisplay(pod.memory)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
